import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import CustomSnackbar from 'components/CustomSnackbar';
import MDTypography from 'components/MDTypography';
import SmallStatusLabel from 'components/SmallStatusLabel/SmallStatusLabel';
import { useEffect, useMemo, useRef, useState } from 'react';
import { brandColors } from 'types/colors';

import { deletePermissionDevice, getPermissionDevices, updatePermissionDevice } from '../../services/usersService';

const ACTIVE_WINDOW_MS = 20 * 60 * 1000;
const selectionCheckboxSx = {
  '& .MuiSvgIcon-root': {
    border: `2px solid ${brandColors.GREY_DARK}`,
  },
  '&.Mui-checked .MuiSvgIcon-root, &.MuiCheckbox-indeterminate .MuiSvgIcon-root': {
    borderColor: brandColors.BLUE,
  },
};

const getIpCountry = async ip => {
  const normalizedIp = String(ip).trim().split('/')[0];
  const response = await fetch(`https://ipwho.is/${encodeURIComponent(normalizedIp)}?fields=success,country,message`);

  if (!response.ok) {
    throw new Error(`Country lookup failed with status ${response.status}`);
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.message || 'Country lookup failed');
  }

  return data.country;
};

const formatLastUpdate = value => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Jerusalem',
    year: '2-digit',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  })
    .formatToParts(date)
    .reduce((result, part) => ({ ...result, [part.type]: part.value }), {});

  return `${parts.hour}:${parts.minute}:${parts.second} ${Number(parts.day)}.${Number(parts.month)}.${parts.year}`;
};

export const PermissionDevicesList = () => {
  const [devices, setDevices] = useState([]);
  const [countries, setCountries] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [updatingRows, setUpdatingRows] = useState([]);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const requestedCountryIps = useRef(new Set());
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const sortedDevices = useMemo(
    () =>
      [...devices].sort((a, b) => {
        const aTime = new Date(a.last_update).getTime();
        const bTime = new Date(b.last_update).getTime();
        const validATime = Number.isNaN(aTime) ? -Infinity : aTime;
        const validBTime = Number.isNaN(bTime) ? -Infinity : bTime;
        return validBTime - validATime;
      }),
    [devices],
  );
  const filteredDevices = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    const selectedAccessFilters = selectedFilters.filter(filter => filter === 'allowed' || filter === 'blocked');
    const selectedActivityFilters = selectedFilters.filter(filter => filter === 'active' || filter === 'inactive');

    return sortedDevices.filter(device => {
      const timestamp = device.last_update ? new Date(device.last_update).getTime() : -Infinity;
      const validTimestamp = Number.isNaN(timestamp) ? -Infinity : timestamp;
      const isActive = validTimestamp <= currentTime && validTimestamp >= currentTime - ACTIVE_WINDOW_MS;

      if (selectedAccessFilters.length > 0) {
        const matchesAccess =
          (selectedAccessFilters.includes('allowed') && device.is_allowed) ||
          (selectedAccessFilters.includes('blocked') && !device.is_allowed);

        if (!matchesAccess) {
          return false;
        }
      }

      if (selectedActivityFilters.length > 0) {
        const matchesActivity =
          (selectedActivityFilters.includes('active') && isActive) || (selectedActivityFilters.includes('inactive') && !isActive);

        if (!matchesActivity) {
          return false;
        }
      }

      if (!normalizedSearch) {
        return true;
      }

      return [device.ip, device.mac, device.name].some(value =>
        String(value || '')
          .toLowerCase()
          .includes(normalizedSearch),
      );
    });
  }, [currentTime, searchText, selectedFilters, sortedDevices]);
  const latestUpdateByMac = useMemo(() => {
    const latestUpdates = new Map();

    devices.forEach(device => {
      if (!device.mac) {
        return;
      }

      const mac = String(device.mac).trim().toLowerCase();
      const timestamp = device.last_update ? new Date(device.last_update).getTime() : -Infinity;
      const validTimestamp = Number.isNaN(timestamp) ? -Infinity : timestamp;

      latestUpdates.set(mac, Math.max(latestUpdates.get(mac) ?? -Infinity, validTimestamp));
    });

    return latestUpdates;
  }, [devices]);
  const selectedDevices = useMemo(
    () => devices.filter(device => selectedRowIds.includes(device.row_id)),
    [devices, selectedRowIds],
  );
  const allFilteredSelected =
    filteredDevices.length > 0 && filteredDevices.every(device => selectedRowIds.includes(device.row_id));
  const someFilteredSelected = filteredDevices.some(device => selectedRowIds.includes(device.row_id));

  const handleSelectDevice = (rowId, checked) => {
    setSelectedRowIds(current => (checked ? [...new Set([...current, rowId])] : current.filter(id => id !== rowId)));
  };

  const handleSelectAllFiltered = checked => {
    const filteredRowIds = filteredDevices.map(device => device.row_id);

    setSelectedRowIds(current =>
      checked ? [...new Set([...current, ...filteredRowIds])] : current.filter(rowId => !filteredRowIds.includes(rowId)),
    );
  };

  const handleFilterChange = filter => {
    setSelectedFilters(current =>
      current.includes(filter) ? current.filter(currentFilter => currentFilter !== filter) : [...current, filter],
    );
  };

  const handlePermissionChange = async (devicesToUpdate, isAllowed) => {
    if (devicesToUpdate.length === 0) {
      return;
    }

    const rowIdsToUpdate = devicesToUpdate.map(device => device.row_id);
    setUpdatingRows(current => [...new Set([...current, ...rowIdsToUpdate])]);

    try {
      const updatedDevices = await updatePermissionDevice({
        devices: devicesToUpdate.map(device => ({
          ip: device.ip,
          mac: device.mac,
        })),
        isAllowed,
      });
      const updatedDevicesByKey = new Map(updatedDevices.map(device => [`${device.ip}|${device.mac}`, device]));
      setDevices(current =>
        current.map(currentDevice => {
          const updatedDevice = updatedDevicesByKey.get(`${currentDevice.ip}|${currentDevice.mac}`);

          return updatedDevice ? { ...currentDevice, ...updatedDevice, row_id: currentDevice.row_id } : currentDevice;
        }),
      );
      setNotification({
        open: true,
        message:
          devicesToUpdate.length === 1
            ? `Access ${isAllowed ? 'allowed' : 'blocked'} for ${devicesToUpdate[0].mac || devicesToUpdate[0].ip || 'device'}`
            : `Access ${isAllowed ? 'allowed' : 'blocked'} for ${devicesToUpdate.length} selected devices`,
        severity: 'success',
      });
    } catch (updateError) {
      setNotification({
        open: true,
        message: updateError.message || 'Failed to update device permission',
        severity: 'error',
      });
    } finally {
      setUpdatingRows(current => current.filter(rowId => !rowIdsToUpdate.includes(rowId)));
    }
  };

  const handleDeleteDevices = async devicesToDelete => {
    if (devicesToDelete.length === 0) {
      return;
    }

    const deviceLabel =
      devicesToDelete.length === 1
        ? devicesToDelete[0].mac || devicesToDelete[0].ip || 'device'
        : `${devicesToDelete.length} selected devices`;
    const confirmed = window.confirm(
      devicesToDelete.length === 1 ? `Delete permission row for ${deviceLabel}?` : `Delete permission rows for ${deviceLabel}?`,
    );
    if (!confirmed) {
      return;
    }

    const rowIdsToDelete = devicesToDelete.map(device => device.row_id);
    setUpdatingRows(current => [...new Set([...current, ...rowIdsToDelete])]);

    try {
      await deletePermissionDevice({
        devices: devicesToDelete.map(device => ({
          ip: device.ip,
          mac: device.mac,
        })),
      });
      setDevices(current => current.filter(currentDevice => !rowIdsToDelete.includes(currentDevice.row_id)));
      setSelectedRowIds(current => current.filter(rowId => !rowIdsToDelete.includes(rowId)));
      setNotification({
        open: true,
        message:
          devicesToDelete.length === 1
            ? `Deleted permission row for ${deviceLabel}`
            : `Deleted ${devicesToDelete.length} permission rows`,
        severity: 'success',
      });
    } catch (deleteError) {
      setNotification({
        open: true,
        message: deleteError.message || 'Failed to delete device permission',
        severity: 'error',
      });
    } finally {
      setUpdatingRows(current => current.filter(rowId => !rowIdsToDelete.includes(rowId)));
    }
  };

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(Date.now()), 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let active = true;

    const loadDevices = async () => {
      try {
        const data = await getPermissionDevices();
        const loadedDevices = Array.isArray(data) ? data : [];
        if (!active) {
          return;
        }

        setDevices(loadedDevices);
      } catch (loadError) {
        if (active) {
          setError(loadError.message || 'Failed to load devices');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadDevices();
    const interval = setInterval(loadDevices, 5 * 60 * 1000);

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    let active = true;
    const uniqueIps = [...new Set(devices.map(device => device.ip).filter(Boolean))];

    uniqueIps.forEach(async ip => {
      if (requestedCountryIps.current.has(ip)) {
        return;
      }
      requestedCountryIps.current.add(ip);

      try {
        const country = await getIpCountry(ip);
        if (active) {
          setCountries(current => ({
            ...current,
            [ip]: country || 'Unavailable',
          }));
        }
      } catch {
        if (active) {
          setCountries(current => ({
            ...current,
            [ip]: 'Unavailable',
          }));
        }
      }
    });

    return () => {
      active = false;
    };
  }, [devices]);

  useEffect(() => {
    setSelectedRowIds(current => current.filter(rowId => devices.some(device => device.row_id === rowId)));
  }, [devices]);

  return (
    <Box sx={{ mt: 3, width: '100%' }}>
      <MDTypography variant="h4" sx={{ color: 'black', mb: 1, py: 1, px: 0.2 }}>
        Devices
      </MDTypography>

      <Box id="permission-devices-content">
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <MDTypography variant="body2" color="error">
            {error}
          </MDTypography>
        )}

        {!loading && !error && devices.length === 0 && (
          <MDTypography variant="body2" color="text">
            No devices to display
          </MDTypography>
        )}

        {!loading && !error && devices.length > 0 && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                gap: 1.5,
                flexDirection: { xs: 'column', sm: 'row' },
                mb: 1.5,
              }}>
              <TextField
                size="medium"
                label="Search devices"
                placeholder="Search by IP, MAC, or name"
                value={searchText}
                onChange={event => setSearchText(event.target.value)}
                sx={{ width: { xs: '100%', sm: 360 } }}
              />
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.75,
                  flexWrap: 'wrap',
                  justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                }}>
                {selectedDevices.length > 0 && (
                  <>
                    <Button
                      variant="inherited"
                      color="success"
                      disabled={selectedDevices.some(device => updatingRows.includes(device.row_id))}
                      onClick={() => handlePermissionChange(selectedDevices, true)}
                      sx={{
                        fontSize: '0.9rem',
                        textTransform: 'none',
                        minHeight: 32,
                        px: 2,
                        whiteSpace: 'nowrap',
                      }}>
                      Allow selected ({selectedDevices.length})
                    </Button>
                    <Button
                      variant="inherited"
                      color="error"
                      disabled={selectedDevices.some(device => updatingRows.includes(device.row_id))}
                      onClick={() => handlePermissionChange(selectedDevices, false)}
                      sx={{
                        fontSize: '0.9rem',
                        textTransform: 'none',
                        minHeight: 32,
                        px: 2,
                        whiteSpace: 'nowrap',
                      }}>
                      Block selected ({selectedDevices.length})
                    </Button>
                    <Button
                      variant="inherited"
                      color="error"
                      startIcon={<DeleteIcon />}
                      disabled={selectedDevices.some(device => updatingRows.includes(device.row_id))}
                      onClick={() => handleDeleteDevices(selectedDevices)}
                      sx={{ fontSize: '0.9rem', textTransform: 'none', minHeight: 32, px: 2, whiteSpace: 'nowrap' }}>
                      Delete selected ({selectedDevices.length})
                    </Button>
                  </>
                )}
                <Button
                  variant="inherit"
                  disableElevation
                  disableRipple
                  disableFocusRipple
                  onClick={() => setSelectedFilters([])}
                  aria-pressed={selectedFilters.length === 0}
                  sx={{
                    fontSize: '0.9rem',
                    borderRadius: '999px',
                    textTransform: 'none',
                    backgroundColor: selectedFilters.length === 0 ? brandColors.BLUE : brandColors.BLUE_LIGHT,
                    color: selectedFilters.length === 0 ? '#fff' : brandColors.BLUE,
                    minHeight: 32,
                    px: 2,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: selectedFilters.length === 0 ? brandColors.BLUE : brandColors.BLUE_LIGHT,
                      opacity: 0.9,
                    },
                    '&:active': {
                      backgroundColor: selectedFilters.length === 0 ? brandColors.BLUE : brandColors.BLUE_LIGHT,
                    },
                    '&.Mui-focusVisible': {
                      boxShadow: 'none',
                    },
                  }}>
                  All
                </Button>
                {[
                  { value: 'blocked', label: 'Blocked devices' },
                  { value: 'allowed', label: 'Allowed devices' },
                  { value: 'active', label: 'Active devices' },
                  { value: 'inactive', label: 'Inactive devices' },
                ].map(option => (
                  <Button
                    key={option.value}
                    variant="inherit"
                    disableElevation
                    disableRipple
                    disableFocusRipple
                    onClick={() => handleFilterChange(option.value)}
                    aria-pressed={selectedFilters.includes(option.value)}
                    sx={{
                      fontSize: '0.9rem',
                      borderRadius: '999px',
                      textTransform: 'none',
                      backgroundColor: selectedFilters.includes(option.value) ? brandColors.BLUE : brandColors.BLUE_LIGHT,
                      color: selectedFilters.includes(option.value) ? '#fff' : brandColors.BLUE,
                      minHeight: 32,
                      px: 2,
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        backgroundColor: selectedFilters.includes(option.value) ? brandColors.BLUE : brandColors.BLUE_LIGHT,
                        opacity: 0.9,
                      },
                      '&:active': {
                        backgroundColor: selectedFilters.includes(option.value) ? brandColors.BLUE : brandColors.BLUE_LIGHT,
                      },
                      '&.Mui-focusVisible': {
                        boxShadow: 'none',
                      },
                    }}>
                    {option.label}
                  </Button>
                ))}
              </Box>
            </Box>
            <MDTypography variant="body2" color="text" sx={{ mb: 1 }}>
              Showing {filteredDevices.length} {filteredDevices.length === 1 ? 'device' : 'devices'}
            </MDTypography>
            {filteredDevices.length === 0 ? (
              <MDTypography variant="body2" color="text">
                No devices match your search
              </MDTypography>
            ) : (
              <TableContainer
                component={Paper}
                sx={{
                  height: {
                    xs: 'calc(100dvh - 340px)',
                    sm: 'calc(100dvh - 315px)',
                  },
                  minHeight: 220,
                }}>
                <Table
                  stickyHeader
                  size="small"
                  aria-label="Device permissions"
                  sx={{
                    minWidth: 940,
                    tableLayout: 'fixed',
                  }}>
                  <colgroup>
                    <col style={{ width: '5%' }} />
                    <col style={{ width: '5%' }} />
                    <col style={{ width: '13%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '13%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '7%' }} />
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">#</TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          checked={allFilteredSelected}
                          indeterminate={!allFilteredSelected && someFilteredSelected}
                          onChange={event => handleSelectAllFiltered(event.target.checked)}
                          inputProps={{ 'aria-label': 'Select all filtered devices' }}
                          sx={selectionCheckboxSx}
                        />
                      </TableCell>
                      <TableCell>IP</TableCell>
                      <TableCell>MAC</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Country</TableCell>
                      <TableCell>Last update</TableCell>
                      <TableCell align="center">Access</TableCell>
                      <TableCell align="center">Delete</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredDevices.map((device, index) => {
                      const updating = updatingRows.includes(device.row_id);
                      const mac = device.mac ? String(device.mac).trim().toLowerCase() : null;
                      const timestamp = device.last_update ? new Date(device.last_update).getTime() : -Infinity;
                      const validTimestamp = Number.isNaN(timestamp) ? -Infinity : timestamp;
                      const hasNewerRow = mac && validTimestamp < latestUpdateByMac.get(mac);
                      const isActive = validTimestamp <= currentTime && validTimestamp >= currentTime - ACTIVE_WINDOW_MS;
                      const selected = selectedRowIds.includes(device.row_id);

                      return (
                        <TableRow
                          key={device.row_id}
                          hover
                          sx={{
                            '& > td:not(:last-of-type)': {
                              opacity: hasNewerRow ? 0.5 : 1,
                            },
                          }}>
                          <TableCell align="center">{index + 1}</TableCell>
                          <TableCell align="center">
                            <Checkbox
                              size="small"
                              checked={selected}
                              disabled={updating}
                              onChange={event => handleSelectDevice(device.row_id, event.target.checked)}
                              inputProps={{
                                'aria-label': `Select permission row for ${device.mac || device.ip || 'device'}`,
                              }}
                              sx={selectionCheckboxSx}
                            />
                          </TableCell>
                          <TableCell>{device.ip || '—'}</TableCell>
                          <TableCell>{device.mac || '—'}</TableCell>
                          <TableCell>{device.name || 'unknown'}</TableCell>
                          <TableCell>{device.ip ? countries[device.ip] || 'Loading…' : 'Unavailable'}</TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                flexWrap: 'wrap',
                              }}>
                              {formatLastUpdate(device.last_update)}
                              <SmallStatusLabel statusLabel={isActive ? 'ACTIVE' : 'INACTIVE'} />
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                              }}>
                              <Switch
                                checked={device.is_allowed}
                                disabled={updating}
                                sx={{
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: theme => `${theme.palette.success.main} !important`,
                                    borderColor: theme => `${theme.palette.success.main} !important`,
                                    opacity: '1 !important',
                                  },
                                }}
                                onChange={event => handlePermissionChange([device], event.target.checked)}
                                inputProps={{
                                  'aria-label': `${device.is_allowed ? 'Block' : 'Allow'} access for ${
                                    device.mac || device.ip || 'device'
                                  }`,
                                }}
                              />
                              <MDTypography
                                color={device.is_allowed ? 'success' : 'text'}
                                sx={{ fontWeight: 700, opacity: hasNewerRow ? 0.5 : 1 }}>
                                {updating ? 'Saving…' : device.is_allowed ? 'Allowed' : 'Blocked'}
                              </MDTypography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              disabled={updating}
                              onClick={() => handleDeleteDevices([device])}
                              aria-label={`Delete permission row for ${device.mac || device.ip || 'device'}`}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Box>

      <CustomSnackbar
        open={notification.open}
        onClose={() => setNotification(current => ({ ...current, open: false }))}
        message={notification.message}
        severity={notification.severity}
        duration={4000}
        vertical="bottom"
        horizontal="left"
        color="#fff"
      />
    </Box>
  );
};
